import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, LabelList
} from 'recharts';

// Helper function to identify company (ASI) models
const isCompanyModel = (modelId) => {
  return modelId === 'asi1_mini' || modelId === 'asi1_mini_0225';
};

// Modern horizontal bar chart component with enhanced design
const ModernBarChart = ({ modelColumns, averageScores, isChartCompact, isMobileView }) => {
  // Function to create compact model names for mobile view
  const getCompactName = (fullName) => {
    if (fullName.startsWith("LLAMA")) {
      const version = fullName.split(' ')[1];
      return `L${version}`;
    } else if (fullName.startsWith("GPT")) {
      return fullName.split(' ').slice(0, 2).join(' ');
    } else {
      return fullName.split(' ')[0];
    }
  };

  // Prepare and sort data
  const chartData = [...modelColumns]
    .map(model => ({
      name: isMobileView ? getCompactName(model.name) : model.name,
      score: Math.round(Number(averageScores[model.id])),
      id: model.id
    }))
    .sort((a, b) => b.score - a.score);

  // Calculate chart dimensions based on device
  const height = isMobileView 
    ? (isChartCompact ? '24rem' : '28rem')
    : (isChartCompact ? '22rem' : '26rem');
  
  // Set appropriate margins based on device
  const margin = isMobileView
    ? { top: 20, right: 60, left: 20, bottom: 5 }
    : { top: 20, right: 60, left: isMobileView ? 20 : 110, bottom: 20 };

  // Calculate bar radius based on size
  const barRadius = 4;
  const barSize = isMobileView ? 14 : 20;

  return (
    <div style={{ height }} className="relative">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={margin}
          barSize={barSize}
          barGap={8}
          className="font-sans"
        >
          <defs>
            {/* Define more sophisticated gradients */}
            <linearGradient id="usincGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#991b1b" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="otherModelGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#1e40af" stopOpacity={1} />
            </linearGradient>
            
            {/* Add subtle pattern overlay for texture */}
            <pattern id="diagonalPattern" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
              <rect width="10" height="10" fill="none"/>
              <path d="M0,0 L0,10 M0,10 L10,10" stroke="white" strokeWidth="0.5" strokeOpacity="0.1" />
            </pattern>
            
            {/* Drop shadow for better depth */}
            <filter id="barShadow" x="-10%" y="-10%" width="120%" height="130%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="rgba(0,0,0,0.2)" />
            </filter>
          </defs>
          
          {/* Dotted grid lines for reference */}
          <CartesianGrid 
            strokeDasharray="2 4" 
            horizontal={true} 
            vertical={false} 
            stroke="#e5e7eb" 
            strokeOpacity={0.6}
          />
          
          {/* X-axis (scores) */}
          <XAxis 
            type="number" 
            domain={[65, 100]} 
            label={{ 
              value: 'MMLU Score (%)', 
              position: 'insideBottom', 
              offset: -5,
              style: { 
                fontWeight: 500, 
                fontSize: 12,
                fill: '#6b7280'
              }
            }}
            tick={{ 
              fontSize: 11, 
              fontWeight: 500,
              fill: '#6b7280'
            }}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
            tickLine={{ stroke: '#d1d5db' }}
          />
          
          {/* Y-axis (model names) */}
          <YAxis 
            type="category" 
            dataKey="name" 
            width={isMobileView ? 75 : 120}
            tick={{ 
              fontSize: isMobileView ? 11 : 12,
              fontWeight: 500,
              fill: '#4b5563',
              lineHeight: 1.2
            }}
            tickLine={false}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
            textAnchor="end"
          />
          
          {/* Enhanced tooltip */}
          <Tooltip 
            formatter={(value) => [`${value}%`, 'MMLU Score']}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '6px',
              padding: '8px 12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              fontSize: '12px',
              fontWeight: 500
            }}
            cursor={{ fill: 'rgba(243, 244, 246, 0.6)' }}
          />
          
          {/* Main score bars */}
          <Bar 
            dataKey="score" 
            name="Average MMLU Score"
            radius={[0, barRadius, barRadius, 0]}
            animationDuration={1000}
            animationEasing="ease-out"
            filter="url(#barShadow)"
          >
            {chartData.map((entry) => {
              const isUSIncModel = isCompanyModel(entry.id);
              return (
                <Cell 
                  key={entry.id}
                  fill={isUSIncModel ? "url(#usincGradient)" : "url(#otherModelGradient)"}
                  style={{ mixBlendMode: 'multiply' }}
                />
              );
            })}
            
            {/* Score labels at end of bars */}
            <LabelList
              dataKey="score"
              position="right"
              formatter={(value) => `${value}%`}
              content={(props) => {
                const { x, y, width, height, value, index } = props;
                const entry = chartData[index];
                const isUSIncModel = isCompanyModel(entry.id);
                
                // Position label outside the bar
                return (
                  <g>
                    <text
                      x={x + width + 12}
                      y={y + height/2}
                      textAnchor="start"
                      dominantBaseline="central"
                      fill={isUSIncModel ? '#b91c1c' : '#1e40af'}
                      fontWeight="600"
                      fontSize={14}
                      filter="url(#barShadow)"
                    >
                      {`${value}%`}
                    </text>
                  </g>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Optional legend for clarity */}
      <div className="absolute bottom-0 right-0 flex items-center gap-4 text-xs font-medium text-gray-500 pr-4 pb-1">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-sm bg-red-600 mr-1"></div>
          <span>Us Inc Models</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-sm bg-blue-600 mr-1"></div>
          <span>Other Models</span>
        </div>
      </div>
    </div>
  );
};

// Main SummaryTab Component
const SummaryTab = ({ 
  modelColumns, 
  averageScores, 
  isMobileView, 
  isChartCompact 
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className={`text-lg font-semibold mb-4 ${isMobileView ? 'text-center' : ''} text-gray-800`}>Overall Average MMLU Scores</h2>
        <div className={`grid ${isMobileView ? 'grid-cols-1 gap-4' : 'grid-cols-2 md:grid-cols-4 gap-5'}`}>
          {modelColumns.map(model => {
            const isUSIncModel = isCompanyModel(model.id);
            const score = Math.round(Number(averageScores[model.id]));
            
            return (
              <div 
                key={model.id} 
                className={`p-4 rounded-lg ${
                  isUSIncModel 
                    ? 'bg-gradient-to-br from-red-50 to-red-100 border border-red-200 shadow-sm'
                    : 'bg-gradient-to-br from-blue-50 to-white border border-gray-200 shadow-sm'
                }`}
              >
                <div className={`flex items-center justify-between ${isMobileView ? 'mb-2' : 'mb-1'}`}>
                  <div className={`${isMobileView ? 'text-sm' : 'text-xs'} uppercase tracking-wider ${
                    isUSIncModel 
                      ? 'text-red-700'
                      : 'text-blue-700'
                  }`}>
                    MODEL
                  </div>
                  {isUSIncModel && (
                    <div className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      Us Inc
                    </div>
                  )}
                </div>
                
                <div className={`${isMobileView ? 'text-base' : 'text-sm'} font-medium text-gray-800 ${
                  isUSIncModel && 'font-semibold'
                }`}>
                  {model.name}
                </div>
                
                <div className="mt-3">
                  <div className={`${isMobileView ? 'text-3xl' : 'text-2xl'} font-bold ${
                    isUSIncModel
                      ? 'text-red-700'
                      : score >= 85 ? 'text-blue-700' : 'text-blue-600'
                  }`}>
                    {score}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bar Chart for Average Scores */}
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className={`text-lg font-semibold mb-6 ${isMobileView ? 'text-center' : ''} text-gray-800`}>Model Performance Comparison</h2>
        
        {/* Modern bar chart for both desktop and mobile */}
        <ModernBarChart 
          modelColumns={modelColumns} 
          averageScores={averageScores} 
          isChartCompact={isChartCompact}
          isMobileView={isMobileView}
        />
      </div>
    </div>
  );
};

export default SummaryTab;
